import * as React from "react"
import { XIcon } from "lucide-react"
import { Dialog as SheetPrimitive } from "radix-ui"

import { cn } from "@/lib/utils"

/**
 * The registry file, with four deliberate local deviations. All are noted here
 * because a future `npx shadcn add sheet --overwrite` will silently undo them.
 *
 * 1. **z-index.** The registry ships `z-50` for both overlay and panel. This
 *    app has a written layer table (see `src/index.css`) in which the app
 *    header is already `z-50`, so a sheet at the same level would depend on
 *    portal DOM order to cover it. Overlay is `z-[60]`, panel `z-[70]`, which
 *    is what the table says and what every other fixed thing here is spaced
 *    against.
 * 2. **Motion.** The `animate-in` / `slide-in-from-*` utilities the registry
 *    uses come from `tw-animate-css`, which is not a dependency of this repo
 *    and is not being added two days before a demo. They are left in place
 *    (they compile to nothing) and the overlay carries `scrim-in`, the
 *    hand-written keyframe already in `index.css`; the panel's rise is passed
 *    in by `<Sheet>` in `kit.tsx` as `sheet-rise`.
 * 3. **The overlay dismisses on click**, rather than only through
 *    `DismissableLayer`. Written out where it happens, below.
 * 4. **The close button carries `aria-label="Close"`.** Written out where it
 *    happens, below.
 */

function Sheet({ ...props }: React.ComponentProps<typeof SheetPrimitive.Root>) {
  return <SheetPrimitive.Root data-slot="sheet" {...props} />
}

function SheetTrigger({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Trigger>) {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />
}

function SheetClose({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Close>) {
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} />
}

function SheetPortal({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Portal>) {
  return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />
}

function SheetOverlay({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Overlay>) {
  return (
    <SheetPrimitive.Overlay
      data-slot="sheet-overlay"
      className={cn(
        "scrim-in fixed inset-0 z-[60] bg-black/50 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0",
        className
      )}
      {...props}
    />
  )
}

function SheetContent({
  className,
  children,
  side = "right",
  showCloseButton = true,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Content> & {
  side?: "top" | "right" | "bottom" | "left"
  showCloseButton?: boolean
}) {
  return (
    <SheetPortal>
      {/* Deviation 3 — see the note at the top of this file.
       *
       * The registry leans entirely on `DismissableLayer`'s outside-pointer
       * handling to dismiss on a scrim tap. Measured on this app, that path
       * drops roughly one tap in ten: Radix arms the document listener from a
       * `setTimeout(0)` and gates it on `isPointerEventsEnabled`, which is
       * false on the render where the new layer's node exists but the previous
       * one has not yet been removed from the module-level `layers` set. In
       * that window the panel also renders `pointer-events: none` — i.e. the
       * sheet is on screen and inert, which is a far worse failure than a
       * scrim that ignores one tap.
       *
       * Wrapping the overlay in `Dialog.Close` makes dismissal a plain click
       * handler on the element the thumb actually lands on, with no layer
       * bookkeeping between the tap and `onOpenChange(false)`. It is idempotent
       * with Radix's own outside-dismiss when that fires. The inertness half is
       * fixed in `kit.tsx`, which pins the panel's inline `pointer-events`. */}
      <SheetPrimitive.Close asChild>
        <SheetOverlay />
      </SheetPrimitive.Close>
      <SheetPrimitive.Content
        data-slot="sheet-content"
        className={cn(
          "fixed z-[70] flex flex-col gap-4 bg-background shadow-lg transition ease-in-out data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:animate-in data-[state=open]:duration-500",
          side === "right" &&
            "inset-y-0 right-0 h-full w-3/4 border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm",
          side === "left" &&
            "inset-y-0 left-0 h-full w-3/4 border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm",
          side === "top" &&
            "inset-x-0 top-0 h-auto border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
          side === "bottom" &&
            "inset-x-0 bottom-0 h-auto border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
          className
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          /* Deviation 4 — `aria-label`, which the registry leaves to the
           * `sr-only` span below. Same string, same accessible name; the
           * attribute is what `tools/sheetsoak.mjs` selects the close button
           * by, and the soak test is the only evidence the sheet layer race is
           * still fixed. Cheaper to name it here than to fork the button. */
          <SheetPrimitive.Close
            aria-label="Close"
            className="absolute top-4 right-4 rounded-xs opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none data-[state=open]:bg-secondary"
          >
            <XIcon className="size-4" />
            <span className="sr-only">Close</span>
          </SheetPrimitive.Close>
        )}
      </SheetPrimitive.Content>
    </SheetPortal>
  )
}

function SheetHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-header"
      className={cn("flex flex-col gap-1.5 p-4", className)}
      {...props}
    />
  )
}

function SheetFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-footer"
      className={cn("mt-auto flex flex-col gap-2 p-4", className)}
      {...props}
    />
  )
}

function SheetTitle({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Title>) {
  return (
    <SheetPrimitive.Title
      data-slot="sheet-title"
      className={cn("font-semibold text-foreground", className)}
      {...props}
    />
  )
}

function SheetDescription({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Description>) {
  return (
    <SheetPrimitive.Description
      data-slot="sheet-description"
      className={cn("text-sm text-muted-foreground", className)}
      {...props}
    />
  )
}

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
}
